const editorialReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

class EditorialMediaCarousel extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.track = this.querySelector('[data-editorial-track]');
    this.slides = [...this.querySelectorAll('[data-editorial-slide]')];
    this.previous = this.querySelector('[data-previous]');
    this.next = this.querySelector('[data-next]');
    if (!this.track || !this.slides.length) return;
    this.toggleAttribute('data-single', this.slides.length < 2);
    this.previous?.addEventListener('click', () => this.move(-1), { signal });
    this.next?.addEventListener('click', () => this.move(1), { signal });
    this.track.addEventListener('scroll', () => this.update(), { passive: true, signal });
    this.addEventListener('shopify:block:select', (event) => {
      const slide = event.target.closest('[data-editorial-slide]');
      if (this.slides.includes(slide)) this.go(this.slides.indexOf(slide));
    }, { signal });
    this.update();
  }

  update() {
    const trackBox = this.track.getBoundingClientRect();
    this.index = this.slides.reduce((best, slide, index) => Math.abs(slide.getBoundingClientRect().left - trackBox.left) < Math.abs(this.slides[best].getBoundingClientRect().left - trackBox.left) ? index : best, 0);
    if (this.previous) this.previous.disabled = this.index === 0;
    if (this.next) this.next.disabled = this.index === this.slides.length - 1;
    this.querySelectorAll('responsive-section-media').forEach((media) => media.sync?.());
  }

  move(delta) { this.go(Math.max(0, Math.min(this.slides.length - 1, this.index + delta))); }
  go(index) {
    const offset = this.slides[index].getBoundingClientRect().left - this.track.getBoundingClientRect().left;
    this.track.scrollBy({ left: offset, behavior: editorialReducedMotion.matches ? 'instant' : 'smooth' });
  }
  disconnectedCallback() { this.controller?.abort(); }
}

class EditorialListicle extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0 || this.scrollWidth <= this.clientWidth || event.target.closest('button,a')) return;
      this.drag = { id: event.pointerId, x: event.clientX, left: this.scrollLeft };
    }, { signal });
    this.addEventListener('pointermove', (event) => {
      if (!this.drag || event.pointerId !== this.drag.id) return;
      const distance = event.clientX - this.drag.x;
      if (Math.abs(distance) < 4 && !this.hasAttribute('data-dragging')) return;
      this.setPointerCapture(event.pointerId);
      this.setAttribute('data-dragging', '');
      this.scrollLeft = this.drag.left - distance;
      event.preventDefault();
    }, { signal });
    const end = () => { this.drag = null; this.removeAttribute('data-dragging'); };
    ['pointerup', 'pointercancel', 'lostpointercapture'].forEach((event) => this.addEventListener(event, end, { signal }));
    this.addEventListener('dragstart', (event) => event.preventDefault(), { signal });
    this.addEventListener('shopify:block:select', (event) => event.target.closest('.editorial-listicle__entry')?.scrollIntoView({ block: 'nearest', inline: 'nearest' }), { signal });
  }
  disconnectedCallback() { this.controller?.abort(); this.drag = null; this.removeAttribute('data-dragging'); }
}

class EditorialScience extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
    this.controller = new AbortController();
    const show = () => { this.classList.remove('is-armed', 'is-revealed'); this.observer?.disconnect(); };
    const reveal = () => { this.classList.remove('is-armed'); this.classList.add('is-revealed'); this.observer?.disconnect(); };
    if (!this.hasAttribute('data-animate') || editorialReducedMotion.matches || window.Shopify?.designMode || !('IntersectionObserver' in window)) { show(); return; }
    this.classList.add('is-armed');
    this.observer = new IntersectionObserver((entries) => { if (entries.some((entry) => entry.isIntersecting)) reveal(); }, { threshold: .15 });
    this.observer.observe(this.querySelector('.editorial-science__callouts') || this);
    editorialReducedMotion.addEventListener('change', show, { signal: this.controller.signal });
  }
  disconnectedCallback() { this.controller?.abort(); this.observer?.disconnect(); this.classList.remove('is-armed'); }
}

class EditorialRoutine extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.panels = [...this.querySelectorAll('[data-routine-panel]')];
    this.tabs = [...this.querySelectorAll('[data-routine-tab]')];
    this.previous = this.querySelector('[data-previous]');
    this.next = this.querySelector('[data-next]');
    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.select(index), { signal });
      tab.addEventListener('keydown', (event) => {
        const keyIndexes = { ArrowLeft: this.index - 1, ArrowRight: this.index + 1, Home: 0, End: this.panels.length - 1 };
        if (!(event.key in keyIndexes)) return;
        event.preventDefault();
        this.select(Math.max(0, Math.min(this.panels.length - 1, keyIndexes[event.key])));
        this.tabs[this.index]?.focus();
      }, { signal });
    });
    this.previous?.addEventListener('click', () => this.select(this.index - 1), { signal });
    this.next?.addEventListener('click', () => this.select(this.index + 1), { signal });
    this.addEventListener('shopify:block:select', (event) => {
      const panel = event.target.closest('[data-routine-panel]');
      if (this.panels.includes(panel)) this.select(this.panels.indexOf(panel));
    }, { signal });
    this.select(0);
  }
  select(index) {
    if (!this.panels.length) return;
    this.index = Math.max(0, Math.min(this.panels.length - 1, index));
    this.panels.forEach((panel, current) => {
      panel.hidden = current !== this.index;
      panel.setAttribute('aria-hidden', String(panel.hidden));
      panel.querySelectorAll('responsive-section-media').forEach((media) => media.sync?.());
    });
    this.tabs.forEach((tab, current) => { tab.setAttribute('aria-pressed', String(current === this.index)); tab.tabIndex = current === this.index ? 0 : -1; });
    if (this.previous) this.previous.disabled = this.index === 0;
    if (this.next) this.next.disabled = this.index === this.panels.length - 1;
    const tab = this.tabs[this.index];
    const tabRail = this.querySelector('[data-routine-tabs]');
    if (tab && tabRail) {
      const tabBox = tab.getBoundingClientRect();
      const railBox = tabRail.getBoundingClientRect();
      const offset = tabBox.left < railBox.left ? tabBox.left - railBox.left : Math.max(0, tabBox.right - railBox.right);
      if (offset) tabRail.scrollBy({ left: offset, behavior: 'instant' });
    }
  }
  disconnectedCallback() { this.controller?.abort(); }
}

for (const [name, element] of [['editorial-media-carousel', EditorialMediaCarousel], ['editorial-listicle', EditorialListicle], ['editorial-science', EditorialScience], ['editorial-routine', EditorialRoutine]]) {
  if (!customElements.get(name)) customElements.define(name, element);
}
